/* The page shape both desks share, from the 12 October hand-off.
 *
 * ONE SHAPE, TWO WIDTHS. The hand-off's rule (README, "Since the last
 * handoff"), as Gabe ruled it on 12 Oct:
 *
 *   - a list page puts its search, filters, sort, export and totals in a 300px
 *     column on the right (`side`), pinned while the page scrolls, and the list
 *     takes the rest of the pane, up to 1320px (furniture.css --d-main-max;
 *     past that the pair centres, or the column pins right: --d-side-pin);
 *   - a page with no such column grows to 1280px and centres;
 *   - nothing takes the whole pane. The hand-off kept quote, project, product
 *     and Demo-authoring pages full width (`width="full"`); Gabe overruled it
 *     on 12 Oct, so a page opened from a list keeps that list's measure
 *     (furniture.css --d-pair-max) and the option is gone.
 *
 * The prototype gets there with scaffolding this file exists to replace:
 * desk-kit.jsx floats `.side` right, then a MutationObserver and a rAF loop
 * measure the column against the first box beside it and write a nudge into a
 * custom property, and a second observer scans the document for table heads to
 * bolt arrows onto. The README says in as many words that neither should ship.
 * Here the column is a slot, the line-up is a grid row plus one :has() rule in
 * furniture.css, and a sortable head is a component a page asks for by name.
 * Nothing measures, nothing observes, and scrolling does no layout work at all,
 * with one sanctioned exception (Gabe, 14 Oct): whether the side column is
 * pinned is pinWhenFits.ts's, which compares the column with its pane when
 * either changes size. It is the only measuring call here, and desk-ui's check
 * holds it to that.
 *
 * WHERE THE SORT HAPPENS IS NOT THIS FILE'S BUSINESS, AND THAT IS THE POINT OF
 * IT. The prototype sorts the rows on screen. The product sorts on the server,
 * so the order covers every page of a list and not just the ten in front of the
 * reader. So the sort state here is a key and a direction and the query string
 * that names them, which a page hands to its own read. The comparator lives with
 * the data, behind an allow-list, where a column nobody meant to be sortable is
 * a 400 and not an accidental full-table scan.
 */

import { useCallback, useRef, useState, type ReactNode } from "react";
import { usePinWhenFits } from "./pinWhenFits.js";

/** Which way a column is ordered. The words a list endpoint takes as `dir`. */
export type SortDir = "asc" | "desc";

/** A list's order: one column and a direction. */
export type ListSort<K extends string = string> = { readonly key: K; readonly dir: SortDir };

/**
 * The order after somebody presses a column's head.
 *
 * The same column again reverses it. A different column starts in the
 * direction its reader most often wants, which is a property of the column and
 * not of the table — newest first for a date, biggest first for money, A to Z
 * for a name — so the page names it in `first` and anything it leaves out
 * starts ascending.
 */
export function toggleSort<K extends string>(
  current: ListSort<K> | null,
  key: K,
  first?: Readonly<Partial<Record<K, SortDir>>>,
): ListSort<K> {
  if (current !== null && current.key === key) {
    return { key, dir: current.dir === "asc" ? "desc" : "asc" };
  }
  return { key, dir: first?.[key] ?? "asc" };
}

/**
 * The query string a list read sends: `sort=<key>&dir=<asc|desc>`.
 *
 * Empty for null, which is "the server's own order". A list that has not been
 * sorted asks exactly what it asked before sorting existed, so a page that
 * gains sortable heads does not change the request its first load makes — and
 * the server, not the page, stays the one place that knows what that order is.
 */
export function sortParams<K extends string>(sort: ListSort<K> | null): string {
  if (sort === null) return "";
  return `sort=${encodeURIComponent(sort.key)}&dir=${sort.dir}`;
}

/**
 * A list's sort, as a page holds it.
 *
 * `rest` is the order the server answers in when nobody has asked — what the
 * heads report to a screen reader before a click, because aria-sort on a list
 * that is in fact newest first should say so. `lit` is whether anybody has
 * asked: until then no head is drawn as the chosen one, which is the
 * prototype's resting head (every arrow quiet), and `params` stays empty so the
 * first read is the plain one.
 */
export function useListSort<K extends string>(
  rest: ListSort<K>,
  first?: Readonly<Partial<Record<K, SortDir>>>,
): {
  readonly sort: ListSort<K>;
  readonly lit: boolean;
  readonly params: string;
  readonly onSort: (key: K) => void;
} {
  const [chosen, setChosen] = useState<ListSort<K> | null>(null);
  // THE FIRST PRESS IS A CHOICE, NOT A REVERSAL. It toggles from what the
  // reader has chosen (nothing, at first), never from `rest`: toggling from the
  // resting order meant pressing the head the list rests on — Created on
  // Orders, Client on Clients — flipped it to the other direction while that
  // head still looked unchosen. Now the first press lights the column in its
  // `first` direction (or ascending), whichever column it is, and the next
  // press on it reverses. A functional update, so two presses before a render
  // both count.
  const onSort = useCallback(
    (key: K) => setChosen((now) => toggleSort(now, key, first)),
    // `first` is a page's constant; a page that writes it inline would
    // otherwise rebuild this on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );
  return { sort: chosen ?? rest, lit: chosen !== null, params: sortParams(chosen), onSort };
}

/**
 * A column head that sorts: a <th> holding a real button, the prototype's arrow,
 * and aria-sort saying what the list is actually ordered by.
 *
 * The arrow is drawn on every sortable head, resting or not — the 12 October
 * hand-off took out the "no arrows until the first click" state Admin's Orders
 * had (admin-commerce.jsx QuietSortTh), because a column that does not look
 * sortable is a column nobody tries. Until somebody has sorted (`lit` false) the
 * arrows are all the quiet two-way mark and no head is drawn as chosen; the
 * order the list arrived in is still reported to a screen reader, because it is
 * still true.
 */
export function SortHead<K extends string>({
  of,
  sort,
  onSort,
  lit = true,
  className,
  children,
}: {
  readonly of: K;
  readonly sort: ListSort<K>;
  readonly onSort: (key: K) => void;
  /** Whether the reader has chosen this order. See useListSort. */
  readonly lit?: boolean;
  /** The design's head classes (`num-h` over a figure). */
  readonly className?: string;
  /** The words on the head. A string, because the button's name is built from it. */
  readonly children: string;
}): JSX.Element {
  const on = sort.key === of;
  const dir = on ? (sort.dir === "asc" ? "ascending" : "descending") : "none";
  const shown = on && lit;
  return (
    <th
      scope="col"
      className={[className, lit ? undefined : "sort-rest"].filter(Boolean).join(" ") || undefined}
      aria-sort={dir}
    >
      <button
        type="button"
        className="th-b"
        aria-label={shown ? `Sorted by ${children}, ${dir}` : `Sort by ${children}`}
        onClick={() => onSort(of)}
      >
        {children}
        <span className="sort" aria-hidden="true">
          {shown ? (sort.dir === "asc" ? "↑" : "↓") : "↕"}
        </span>
      </button>
    </th>
  );
}

/**
 * A page: its head across the top, then the content, and — on a list page — the
 * right-hand column beside it.
 *
 * `head` is everything that spans the page above both columns: the PageHead or
 * the `.adm-bar` title row, and anything the design draws full width before the
 * list (Clients' Add a client). `side` is the 300px column. It comes first in
 * the document, so below 1100px, where it sits above the list rather than
 * beside it, the search is the first thing under the title; furniture.css moves
 * it to the right from 1100px up. `pager` goes at the foot of the list column:
 * at the bottom of the screen when the list is short, directly under the last
 * row when the list is long, and never floating over it.
 *
 * THE COLUMN LINES UP WITH THE FIRST BOX BESIDE IT, NOT WITH A HEADING. When the
 * list column opens on a section label, the side column drops by that label's
 * height, so its top meets the table's top. That is a stylesheet rule
 * (furniture.css, `.desk-cols:has(...)`), not a measurement, which is why a
 * section label is one height with or without its (i).
 *
 * THE COLUMN IS PINNED ONLY WHILE IT FITS. Beside the list it stays under the
 * top bar while the whole of it fits the pane, and otherwise scrolls with the
 * page; it never scrolls on its own, so it draws no second scroll bar and none
 * of it is out of reach (pinWhenFits.ts says why).
 */
export function DeskPage({
  head,
  side,
  pager,
  foot,
  className,
  children,
}: {
  readonly head?: ReactNode;
  /** The right-hand column: search, filters, sort, export, totals. */
  readonly side?: ReactNode;
  /**
   * The list's Pager, placed by the page-shape rule. ALWAYS HERE, NEVER IN
   * `children`: a Pager among the children sits wherever the list happens to
   * end, which on a short list is half way up the screen. desk-ui's check
   * refuses a Pager written inside a DeskPage's children anywhere in the repo.
   */
  readonly pager?: ReactNode;
  /**
   * What follows the list and its pager in the list column, at the foot of the
   * page (Inventory's Maintenance). Kept out of `children` so the pager stays
   * against the list it pages.
   */
  readonly foot?: ReactNode;
  readonly className?: string;
  readonly children?: ReactNode;
}): JSX.Element {
  const hasSide = side !== undefined && side !== null && side !== false;
  const sideRef = useRef<HTMLElement>(null);
  usePinWhenFits(sideRef, [hasSide]);
  const classes = [
    "stack",
    "desk-page",
    hasSide ? "desk-page--side" : undefined,
    className,
  ]
    .filter(Boolean)
    .join(" ");
  const pagerBlock = pager !== undefined && pager !== null && pager !== false ? <div className="desk-pager">{pager}</div> : null;
  const footBlock = foot !== undefined && foot !== null && foot !== false ? <div className="desk-foot">{foot}</div> : null;
  return (
    <div className={classes}>
      {head}
      {hasSide ? (
        <div className="desk-cols">
          <aside className="side" ref={sideRef}>
            {side}
          </aside>
          <div className="desk-main">
            {children}
            {pagerBlock}
            {footBlock}
          </div>
        </div>
      ) : (
        <div className="desk-main">
          {children}
          {pagerBlock}
          {footBlock}
        </div>
      )}
    </div>
  );
}
